import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, AlertCircle, RefreshCw, Database, Zap } from 'lucide-react';

interface SyncProgress {
  syncId: string;
  userId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  progress: number;
  totalRecords: number;
  processedRecords: number;
  currentStep: string;
  errorMessage?: string;
  startedAt: string;
  completedAt?: string;
}

interface DataSyncStatusProps {
  onSyncComplete?: () => void;
  showRefreshButton?: boolean;
}

export function DataSyncStatus({ onSyncComplete, showRefreshButton = false }: DataSyncStatusProps) {
  const [syncStatus, setSyncStatus] = useState<SyncProgress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const API_BASE = 'https://instantly-analytics-api-dev.theo-213.workers.dev';

  const checkSyncStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/sync/status`, {
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSyncStatus(result.data);
          
          // Check if sync is complete
          if (result.data.status === 'completed' && onSyncComplete) {
            onSyncComplete();
          }
        }
      }
    } catch (error) {
      console.error('Failed to check sync status:', error);
    }
  };

  const startDataSync = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/sync/start`, {
        method: 'POST',
        credentials: 'include',
      });

      const result = await response.json();

      if (result.success) {
        // Start polling for status
        setSyncStatus({
          syncId: 'temp',
          userId: 'temp',
          status: 'pending',
          progress: 0,
          totalRecords: 0,
          processedRecords: 0,
          currentStep: 'Starting sync...',
          startedAt: new Date().toISOString()
        });
      } else {
        setError(result.error || 'Failed to start sync');
      }
    } catch (error) {
      console.error('Failed to start sync:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Poll for sync status updates
  useEffect(() => {
    if (syncStatus && (syncStatus.status === 'pending' || syncStatus.status === 'in_progress')) {
      const interval = setInterval(checkSyncStatus, 2000); // Poll every 2 seconds
      return () => clearInterval(interval);
    }
  }, [syncStatus]);

  // Check initial sync status on mount
  useEffect(() => {
    checkSyncStatus();
  }, []);

  const getStatusIcon = () => {
    if (!syncStatus) return null;

    switch (syncStatus.status) {
      case 'pending':
      case 'in_progress':
        return <Loader2 className="w-5 h-5 animate-spin text-blue-600" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    if (!syncStatus) return 'bg-gray-100';

    switch (syncStatus.status) {
      case 'pending':
      case 'in_progress':
        return 'bg-blue-50 border-blue-200';
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return 'N/A';
    // The timestamp might be in various formats, so we handle them robustly
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return date.toLocaleString(); // Format as '7/3/2025, 7:20:52 PM'
  };

  if (!syncStatus && !showRefreshButton) {
    return null;
  }

  return (
    <div className={`border rounded-lg p-4 ${getStatusColor()}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-gray-600" />
          <span className="font-medium text-gray-900">Data Sync Status</span>
        </div>
        
        {showRefreshButton && (
          <button
            onClick={startDataSync}
            disabled={isLoading || (syncStatus?.status === 'in_progress')}
            className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-200 rounded-md p-3 mb-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {syncStatus && (
        <div className="space-y-3">
          {/* Status and Progress */}
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">
                  {syncStatus.status === 'pending' && 'Preparing sync...'}
                  {syncStatus.status === 'in_progress' && syncStatus.currentStep}
                  {syncStatus.status === 'completed' && 'Sync completed successfully'}
                  {syncStatus.status === 'failed' && 'Sync failed'}
                </span>
                {syncStatus.status === 'in_progress' && (
                  <span className="text-sm text-gray-500">{syncStatus.progress}%</span>
                )}
              </div>
              
              {(syncStatus.status === 'in_progress' || syncStatus.status === 'completed') && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${syncStatus.progress}%` }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Additional Info */}
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">Started:</span> {formatTime(syncStatus.startedAt)}
            </div>
            {syncStatus.completedAt && (
              <div>
                <span className="font-medium">Completed:</span> {formatTime(syncStatus.completedAt)}
              </div>
            )}
            {syncStatus.totalRecords > 0 && (
              <div>
                <span className="font-medium">Records:</span> {syncStatus.processedRecords}/{syncStatus.totalRecords}
              </div>
            )}
          </div>

          {/* Error Message */}
          {syncStatus.status === 'failed' && syncStatus.errorMessage && (
            <div className="bg-red-100 border border-red-200 rounded-md p-3">
              <p className="text-red-700 text-sm">{syncStatus.errorMessage}</p>
            </div>
          )}

          {/* Success Message */}
          {syncStatus.status === 'completed' && (
            <div className="flex items-center gap-2 text-green-700 text-sm">
              <Zap className="w-4 h-4" />
              <span>Your InstantlyAI data has been synced and analyzed!</span>
            </div>
          )}
        </div>
      )}

      {!syncStatus && !isLoading && (
        <div className="text-center py-4">
          <p className="text-gray-600 mb-3">No recent sync found</p>
          <button
            onClick={startDataSync}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mx-auto"
          >
            <Database className="w-4 h-4" />
            Start Data Sync
          </button>
        </div>
      )}
    </div>
  );
}